// Print-only route (backlog PRINT-26): what Cloudflare's Browser Run
// service navigates to when generate-document-pdf asks it to render a
// document as a PDF. Deliberately bare — no AppShell, no interactive
// chrome (buttons, status menu, dialogs) — nothing here is ever seen by a
// real user, only by the render service. Renders the exact same
// QuoteSheet/AiaSheet components DocumentViewPage.tsx renders on-screen
// (reusing them is the whole point — it's what keeps the server-rendered
// PDF from drifting out of sync with the on-screen preview), branching on
// docType the same way DocumentViewPage does.
//
// No Supabase session: this route is reached via a short-lived signed
// token in the query string (minted by generate-document-pdf), not a
// logged-in user — see _shared/renderToken.ts for why a real session would
// be the wrong shape here (RLS-scoped to whatever that identity can reach,
// not just this one document). It fetches from print-doc-data with a plain
// fetch() rather than the Supabase client (which would attach anon-key/
// session headers this route has no use for), and that function has
// already resolved every photo/logo signed URL server-side — this page
// hands them straight through to QuoteSheet/AiaSheet's resolvedPhotoUrls/
// resolvedLogoUrl props instead of letting those components try to
// resolve them itself (which would need a session this page doesn't have).
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AiaSheet } from "@/components/snapdraw/aia-sheet";
import { QuoteSheet } from "@/components/snapdraw/quote-sheet";
import { supabaseUrl } from "@/lib/supabase";
import type { AnyDocument } from "@/lib/types";
import "@/styles/tailwind-preview.css";

interface PrintDocData {
  doc: AnyDocument;
  resolvedPhotoUrls: Record<string, string>;
  resolvedLogoUrl: string | null;
  predecessorLabel: string | null;
}

export function PrintDocumentPage() {
  const { docKey } = useParams<{ docKey: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // undefined = still loading, null = failed/missing (bad token, expired,
  // deleted doc, network error) — same three-state shape as
  // DocumentViewPage's own `doc` state, for the same reason.
  const [data, setData] = useState<PrintDocData | null | undefined>(undefined);

  useEffect(() => {
    if (!docKey || !token) {
      setData(null);
      return;
    }
    let cancelled = false;
    const url = `${supabaseUrl}/functions/v1/print-doc-data?docKey=${encodeURIComponent(docKey)}&token=${encodeURIComponent(token)}`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`print-doc-data responded ${res.status}`);
        return (await res.json()) as PrintDocData;
      })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((error: unknown) => {
        console.error("PrintDocumentPage: failed to load document data", error);
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [docKey, token]);

  // No visible content yet — the render service will wait for the DOM to
  // settle before printing regardless, and this route has no user to show
  // a spinner to.
  if (data === undefined) return null;

  if (!data) {
    // Deliberately plain, not a branded EmptyState component — this text
    // is never seen by a real user, only ever by the render service (and,
    // rarely, a human debugging a failed render directly).
    return <p style={{ padding: 24, fontFamily: "monospace" }}>This document could not be loaded.</p>;
  }

  const { doc, resolvedPhotoUrls, resolvedLogoUrl, predecessorLabel } = data;

  return doc.docType === "g702" ? (
    <AiaSheet doc={doc} resolvedLogoUrl={resolvedLogoUrl} />
  ) : (
    <QuoteSheet doc={doc} resolvedPhotoUrls={resolvedPhotoUrls} resolvedLogoUrl={resolvedLogoUrl} predecessorLabel={predecessorLabel} />
  );
}
